import { CSSProperties, RefObject, useLayoutEffect, useState } from "react";

export type AnchoredAlignT = "start" | "end";

export type AnchoredPositionT = { top: number; left: number; direction: "ltr" | "rtl" };

/** Gap between the anchor and the popover, and the margin kept from the viewport edges. */
const GAP = 4;

// spec-49 DEFERRED §5 FINDING — a popover that is `position: absolute` inside its anchor is clipped by ANY
// scrolling ancestor: `Versions/styles.module.scss .tableWrapper` sets `overflow-x: auto`, and per CSS a
// non-`visible` value on one axis forces the other to compute to `auto`, so that wrapper clips vertically too
// and cut off both the row ⋮ menu and the lineage list whenever the results table was shorter than they are.
// Dropping the wrapper's overflow is not an option — a narrow desktop window genuinely needs to scroll the
// table sideways — so the popovers escape instead: portalled to <body> and fixed to the viewport.
//
// Returns null until the popover has been measured; it must therefore be rendered (hidden) for one layout
// pass. `anchoredPopoverStyle` below does that, before the browser paints, so nothing flickers.
export const useAnchoredPosition = (
  open: boolean,
  anchorRef: RefObject<HTMLElement>,
  popoverRef: RefObject<HTMLElement>,
  align: AnchoredAlignT
): AnchoredPositionT | null => {
  const [position, setPosition] = useState<AnchoredPositionT | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    const place = () => {
      const anchor = anchorRef.current;
      const popover = popoverRef.current;
      if (!anchor || !popover) {
        return;
      }
      const a = anchor.getBoundingClientRect();
      const p = popover.getBoundingClientRect();
      // The portal sits at <body>, outside the `.rtl` wrapper App puts the direction on, so read the
      // direction off the anchor and carry it — the same thing Popup does with its `dir`.
      const direction = window.getComputedStyle(anchor).direction === "rtl" ? "rtl" : "ltr";

      // What `inset-inline-start: 0` / `inset-inline-end: 0` did before the popover was portalled.
      const alignsToLeftEdge = align === (direction === "rtl" ? "end" : "start");
      const aligned = alignsToLeftEdge ? a.left : a.right - p.width;
      const left = Math.max(GAP, Math.min(aligned, window.innerWidth - p.width - GAP));

      // Below the anchor; above it when there is no room below.
      const below = a.bottom + GAP;
      const above = a.top - GAP - p.height;
      const top =
        below + p.height > window.innerHeight - GAP && above >= GAP
          ? above
          : Math.max(GAP, Math.min(below, window.innerHeight - p.height - GAP));

      setPosition({ top, left, direction });
    };
    place();
    // `capture` so a scroll of any ancestor container — not just the window — moves it with the anchor.
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, align]);

  return position;
};

export const anchoredPopoverStyle = (position: AnchoredPositionT | null): CSSProperties => ({
  position: "fixed",
  top: position?.top ?? 0,
  left: position?.left ?? 0,
  // Hidden for the single layout pass that measures it — the layout effect runs before the browser paints.
  visibility: position ? undefined : "hidden",
});
